import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  useGetGeneralSurveyById,
  useGetSurveyMetrics,
  useGetSurveyQuestionResponseStatistics,
  useSurveyLocationStatistics,
} from '@occc-app/dataAccessLayer/survey/queries';
import { LoadedData } from '@occc-app/routes/Wallet/types';
import {
  AudienceBasicFragment,
  GeneralSurveyFragment,
  LocationFragment,
  Sort_Order,
  Survey_Location_Statistics_Sort_By,
  Survey_Metrics_Period,
  SurveyLocationStatisticsFragment,
  SurveyLocationStatisticsSort,
  SurveyMetricsFragment,
} from '@occc/graphql';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { calcDateTime, formatToUTCDateString } from '@occc-app/lib/date';

import { useGetAudienceNameByUuid } from '@occc-app/dataAccessLayer/audience/use-get-audience-name-by-uuid.hook';
import { SurveyDetailsPage } from './SurveyDetailsPage';
import { useSurveyLocationSortingParams } from './hooks';
import {
  ChartType,
  fallbackQuestionType,
  QuestionTypeToChartTypeMap,
  SurveyStatisticsBarChartItem,
  SurveyStatisticsPieChartItem,
  DateRange,
} from './types';

import { convertStatisticsDataToChartItem } from './utils/convertStatisticsDataToChartItem';
import {
  getMetricsFilterMaxSelectableDate,
  getMetricsFilterMinSelectableDate,
} from './utils/metricsFilterDateBounds';
import { ArchiveSurveyConfirmDialog } from '../components/ArchiveSurveyConfirmDialog';
import { ActivateSurveyConfirmDialog } from '../components/ActivateSurveyConfirmDialog';
import { useArchiveGeneralSurvey } from '../hooks/useArchiveGeneralSurvey';
import { useActivateGeneralSurvey } from '../hooks/useActivateGeneralSurvey';
import {
  SURVEY_LOCATIONS_AFTER_PARAM,
  SURVEY_LOCATIONS_BEFORE_PARAM,
  SURVEY_LOCATIONS_PAGE_SIZE,
} from '../constants';
import { SortingProps } from '../../../hooks/useSortingParams';

export const SurveyDetailsPageContainer = () => {
  const [isArchiveConfirmation, setIsArchiveConfirmation] = React.useState(false);
  const [isActivateConfirmation, setIsActivateConfirmation] = React.useState(false);

  const { id: surveyId = '' } = useParams<{ id: string }>();

  const [metricsLocations, setMetricsLocations] = React.useState<LocationFragment[]>([]);
  const [period, setPeriod] = React.useState<Survey_Metrics_Period>(Survey_Metrics_Period.AllTime);

  const [dateRange, setDateRange] = React.useState<DateRange>(() => {
    const now = new Date();
    return {
      startDate: calcDateTime(now, 'sub', 1, 'week'),
      endDate: calcDateTime(now, 'sub', 1, 'day'),
    };
  });

  const { survey, loading, error } = useGetGeneralSurveyById({
    variables: { uuid: surveyId },
    skip: !surveyId,
  });

  const metricsFilterMaxSelectableDate = useMemo(
    () => getMetricsFilterMaxSelectableDate(survey),
    [survey],
  );

  const metricsFilterMinSelectableDate = useMemo(
    () => getMetricsFilterMinSelectableDate(survey, metricsFilterMaxSelectableDate),
    [survey, metricsFilterMaxSelectableDate],
  );

  const chartType: ChartType =
    QuestionTypeToChartTypeMap[survey?.questions[0].__typename || fallbackQuestionType];

  const { statistics } = useGetSurveyQuestionResponseStatistics({
    variables: { questionUuid: survey?.questions[0].uuid || '' },
  });

  const audienceUuid = survey?.audienceUuid;

  const audienceResponse = useGetAudienceNameByUuid(audienceUuid);

  const [locationStatisticsPaginationInfo, onLocationStatisticsPageChange] = usePaginationParams({
    pageSize: SURVEY_LOCATIONS_PAGE_SIZE,
    beforeParamName: SURVEY_LOCATIONS_BEFORE_PARAM,
    afterParamName: SURVEY_LOCATIONS_AFTER_PARAM,
  });
  const [locationSortingParams, setLocationSortingParams] = useSurveyLocationSortingParams();
  const sortingProps: SortingProps | undefined = useMemo(() => {
    return locationSortingParams
      ? {
          field: locationSortingParams.field,
          direction: locationSortingParams.direction,
          onClick: (field: Survey_Location_Statistics_Sort_By) => {
            if (locationSortingParams.field !== field) {
              setLocationSortingParams({ field, direction: 'desc' });
              return;
            }
            setLocationSortingParams({
              field,
              direction: locationSortingParams.direction === 'asc' ? 'desc' : 'asc',
            });
          },
        }
      : undefined;
  }, [locationSortingParams, setLocationSortingParams]);

  const locationSort: SurveyLocationStatisticsSort | undefined = React.useMemo(() => {
    if (!locationSortingParams) return undefined;
    return {
      field: locationSortingParams.field,
      direction: locationSortingParams.direction === 'desc' ? Sort_Order.Desc : Sort_Order.Asc,
    };
  }, [locationSortingParams]);

  const locationStatisticsResponse = useSurveyLocationStatistics({
    paginationInfo: locationStatisticsPaginationInfo,
    onPageChange: onLocationStatisticsPageChange,
    baseOptions: {
      variables: { filter: { surveyUuidEquals: surveyId }, sort: locationSort },
    },
  });

  const metricsLocationsFilter = React.useMemo(() => {
    if (metricsLocations.length === 0) {
      return undefined;
    }

    return metricsLocations.map((location) => location.uuid);
  }, [metricsLocations]);

  const metricsDateRangeFilter = React.useMemo(() => {
    if (period === Survey_Metrics_Period.Custom && dateRange) {
      return {
        startDate: formatToUTCDateString(dateRange.startDate),
        endDate: formatToUTCDateString(calcDateTime(dateRange.endDate, 'add', 1, 'day')),
      };
    }

    return undefined;
  }, [dateRange, period]);

  const surveyMetricsResponse = useGetSurveyMetrics({
    variables: {
      filter: {
        surveyUuidEquals: surveyId,
        period,
        locationUuidContains: metricsLocationsFilter,
        dateRange: metricsDateRangeFilter,
      },
    },
  });

  const onCloseArchiveConfirmationDialog = React.useCallback(() => {
    setIsArchiveConfirmation(false);
  }, []);

  const onCloseActivateConfirmationDialog = React.useCallback(() => {
    setIsActivateConfirmation(false);
  }, []);

  const [archiveSurvey, archiveSurveyLoading] = useArchiveGeneralSurvey({
    onCompleted: onCloseArchiveConfirmationDialog,
    refetchQueries: ['generalSurvey'],
  });

  const [activateSurvey, activateSurveyLoading] = useActivateGeneralSurvey({
    onCompleted: onCloseActivateConfirmationDialog,
    refetchQueries: ['generalSurvey'],
  });

  const onArchiveSurvey = React.useCallback(() => {
    archiveSurvey(surveyId);
  }, [surveyId, archiveSurvey]);

  const onActivateSurvey = React.useCallback(() => {
    activateSurvey(surveyId);
  }, [surveyId, activateSurvey]);

  const openArchiveConfirmationDialog = React.useCallback(() => {
    setIsArchiveConfirmation(true);
  }, []);

  const openActivateConfirmationDialog = React.useCallback(() => {
    setIsActivateConfirmation(true);
  }, []);

  const loadedSurveyData: LoadedData<GeneralSurveyFragment> = React.useMemo(() => {
    return {
      loading: loading,
      error: error,
      data: survey,
    };
  }, [survey, error, loading]);

  const loadedAudienceData: LoadedData<AudienceBasicFragment> = React.useMemo(() => {
    return {
      loading: audienceResponse.loading,
      error: audienceResponse.error,
      data: audienceResponse.data,
    };
  }, [audienceResponse.data, audienceResponse.error, audienceResponse.loading]);

  const loadedLocationStatisticsData: LoadedData<SurveyLocationStatisticsFragment[]> =
    React.useMemo(() => {
      return {
        loading: locationStatisticsResponse.loading,
        error: locationStatisticsResponse.error,
        data: locationStatisticsResponse.data,
      };
    }, [
      locationStatisticsResponse.data,
      locationStatisticsResponse.error,
      locationStatisticsResponse.loading,
    ]);

  const responseStatisticsPerLocationMap: Record<
    string,
    SurveyStatisticsPieChartItem | SurveyStatisticsBarChartItem
  > = {};
  locationStatisticsResponse.data?.forEach(
    ({ siteId, responseStatistics: { sliderResponseRanges, optionSelectionCounters } }) => {
      responseStatisticsPerLocationMap[siteId] = convertStatisticsDataToChartItem({
        chartType,
        optionSelectionCounters,
        sliderResponseRanges,
      });
    },
  );

  const allAnswersChartSectionProps = useMemo<
    SurveyStatisticsPieChartItem | SurveyStatisticsBarChartItem | null
  >(() => {
    const chart = convertStatisticsDataToChartItem({
      chartType,
      optionSelectionCounters: statistics?.optionSelectionCounters,
      sliderResponseRanges: statistics?.sliderResponseRanges,
    });

    const containsOptionsWithAnswers = chart.items.some(({ value }) => value > 0);

    return containsOptionsWithAnswers ? chart : null;
  }, [chartType, statistics?.optionSelectionCounters, statistics?.sliderResponseRanges]);

  const loadedSurveyMetricsData: LoadedData<SurveyMetricsFragment> = React.useMemo(() => {
    return {
      loading: surveyMetricsResponse.loading,
      error: surveyMetricsResponse.error,
      data: surveyMetricsResponse.data?.surveyMetrics,
    };
  }, [surveyMetricsResponse.data, surveyMetricsResponse.error, surveyMetricsResponse.loading]);

  return (
    <>
      <SurveyDetailsPage
        surveyData={loadedSurveyData}
        audienceData={loadedAudienceData}
        locationStatisticsData={loadedLocationStatisticsData}
        responseStatisticsPerLocationMap={responseStatisticsPerLocationMap}
        allAnswersChartSectionProps={allAnswersChartSectionProps}
        surveyMetricsData={loadedSurveyMetricsData}
        locationStatisticsPaginationProps={locationStatisticsResponse.paginationProps}
        sortingProps={sortingProps}
        onActivate={openActivateConfirmationDialog}
        onArchive={openArchiveConfirmationDialog}
        onPeriodChange={setPeriod}
        customDateRange={dateRange}
        metricsFilterMinSelectableDate={metricsFilterMinSelectableDate}
        metricsFilterMaxSelectableDate={metricsFilterMaxSelectableDate}
        onCustomDateRangeChange={setDateRange}
        metricsLocations={metricsLocations}
        onMetricsLocationsChange={setMetricsLocations}
      />

      {isArchiveConfirmation && (
        <ArchiveSurveyConfirmDialog
          onClose={onCloseArchiveConfirmationDialog}
          onConfirm={onArchiveSurvey}
          loading={archiveSurveyLoading}
          surveyName={survey?.name ?? ''}
        />
      )}

      {isActivateConfirmation && (
        <ActivateSurveyConfirmDialog
          onClose={onCloseActivateConfirmationDialog}
          onConfirm={onActivateSurvey}
          loading={activateSurveyLoading}
        />
      )}
    </>
  );
};
